import {
    createContext,
    useContext,
    type FocusEventHandler,
    type ReactNode,
} from "react";
import type {
    PickerChevron,
    PickerClassNames,
    PickerForcedState,
    PickerItemValue,
    PickerParentSelection,
    PickerRootKind,
    PickerStatus,
    PickerTypedCommitReason,
} from "./Picker.types";

/** What a rendered item tells the root about itself, kept after unmount. */
export interface PickerItemRegistration {
    value: PickerItemValue;
    label?: string;
    isDisabled: boolean;
}

/**
 * Root-level state every Picker part reads: which Base UI family to render,
 * the current selection, and the callbacks that let sections and triggers
 * act on it without knowing the root's value generic.
 */
export interface PickerRootContextValue {
    kind: PickerRootKind;
    isMultiple: boolean;
    /** True under `allowCustomValue`: the committed value is the input text, not a selected row. */
    isTextValue: boolean;
    isDisabled: boolean;
    isInvalid: boolean;
    /** Whether parent rows of a tree hold a value of their own (`selectableParents`). */
    isParentSelectable: boolean;
    /** Multi-select parents: their own value (`self`) or a toggle for their leaves (`cascade`). */
    parentSelection: PickerParentSelection;
    /** Under `cascade`: each parent value to the enabled leaves its checkbox toggles. */
    cascadeDescendants?: ReadonlyMap<
        PickerItemValue,
        readonly PickerItemValue[]
    >;
    /** Under `self` with `indicateSelectedDescendants`: each parent value to every value below it. */
    indicatedDescendants?: ReadonlyMap<
        PickerItemValue,
        readonly PickerItemValue[]
    >;
    /** Each value of an exclusive section to its siblings; those rows draw the check under `multiple`. */
    exclusiveSiblings?: ReadonlyMap<
        PickerItemValue,
        readonly PickerItemValue[]
    >;
    selectedValues: readonly PickerItemValue[];
    /** Set-backed membership test; prefer it to scanning `selectedValues`. */
    isSelected: (value: PickerItemValue) => boolean;
    setSelectedValues: (values: readonly PickerItemValue[]) => void;
    registerItem: (registration: PickerItemRegistration) => void;
    isValueDisabled: (value: PickerItemValue) => boolean;
    selectionLabel: string | null;
    /** A non-string `renderValue` result, shown by button triggers in place of the label. */
    selectionNode?: ReactNode;
    /** Whether this root's popup is showing (inline roots always are). */
    isPopupOpen: boolean;
    classNames: PickerClassNames;
    /** Marks the trigger (`data-picker-trigger`) so blur can tell it from outside focus. */
    chainId: string;
    /** Trigger blur: typed-text commit, then the root's focus-leave `onBlur`. */
    onTriggerBlur?: FocusEventHandler<HTMLElement>;
    /** Popup focus and blur (flyout levels and portalled overlays bubble here). */
    onPopupFocus: () => void;
    onPopupBlur: FocusEventHandler<HTMLElement>;
    openPopup: () => void;
    closePopup: () => void;
    /** Seeds the search text of Combobox-kind roots (type-to-open on a cell trigger). */
    setInputValue?: (text: string) => void;
    /** Set by a mounted `Picker.Search`; `null` means the popup holds no search input. */
    searchAutoFocusRef: { current: boolean | null };
    /** `Picker.Select inline`: the list renders in place, with no trigger and no popup. */
    isInline: boolean;
    /**
     * Inline roots only: whether a `Picker.Search` is mounted. Without one the
     * list itself is the root's tab stop and takes the navigation keys.
     */
    hasInlineSearch: boolean;
    setHasInlineSearch: (hasSearch: boolean) => void;
    /**
     * Whether the popup hands focus back to the trigger when it closes: true
     * for Escape and item selection, false after an outside press or focus
     * moving away, so a click elsewhere does not pull focus back into the
     * field.
     */
    returnFocusOnCloseRef: { current: boolean };
    /** Field chrome the popup anchors to when an input trigger is in use; `null` anchors to the Base UI trigger. */
    triggerAnchor: HTMLElement | null;
    setTriggerAnchor: (element: HTMLElement | null) => void;
    /** Playground-only forced state the trigger preset and the first row paint. */
    forcedState?: PickerForcedState;
    /** Chevron mode the trigger presets read unless they set `showChevron`. */
    chevron: PickerChevron;
    /** The root's `chevron` prop as the consumer passed it (the cell presets' only root input). */
    explicitChevron?: PickerChevron;
    /** More rows exist past the loaded ones (`hasMore`). */
    hasMore: boolean;
    /** State of the next-page request (`loadMoreStatus`). */
    loadMoreStatus: PickerStatus;
    /** Stable; calls the latest `onLoadMore`. */
    onLoadMore: () => void;
}

export const PickerRootContext = createContext<PickerRootContextValue | null>(
    null,
);

/**
 * The per-keystroke half of a root's state, kept apart from
 * `PickerRootContext` so rows (which never read it) stay memoised while the
 * user types; only the input parts and the level scoping read it.
 */
export interface PickerQueryContextValue {
    /** Current search / typed text of Combobox-kind roots; empty for a plain button trigger. */
    inputValue: string;
    /** The text that actually filters this level's rows (empty when the input only shows the selected label). */
    filterQuery: string;
    /** What the user typed: empty while an input trigger only shows its selected label, even on roots that search externally. */
    searchText: string;
    /** The top root's filtering text, which `path` scoping and auto-open follow in every level. */
    topQuery: string;
    /** `emptyBehavior="hide"` with nothing to show (no row, footer or status): the popup cannot open. */
    isPopupHidden: boolean;
    /** Text triggers only: commits the exact-label match of the typed text on Enter or blur. */
    commitTypedText?: (reason: PickerTypedCommitReason) => boolean;
}

export const PickerQueryContext = createContext<PickerQueryContextValue | null>(
    null,
);

/** Reads the active root's query half; throws outside a root like `usePickerRoot`. */
export const usePickerQuery = (): PickerQueryContextValue => {
    const context = useContext(PickerQueryContext);

    if (context === null) {
        throw new Error("Picker parts must be rendered inside a Picker root");
    }

    return context;
};

/** Exhaustiveness escape for the root-kind switches. */
export const unsupportedPickerKind = (kind: PickerRootKind): never => {
    throw new Error(`Picker root kind "${kind}" is not available yet`);
};

/** Reads the active root; throws so a stray part fails loudly instead of rendering nothing. */
export const usePickerRoot = (): PickerRootContextValue => {
    const context = useContext(PickerRootContext);

    if (context === null) {
        throw new Error("Picker parts must be rendered inside a Picker root");
    }

    return context;
};

/**
 * A cell preset's chevron mode: its own `showChevron`, else the root's
 * `chevron` prop as passed, else `none` (legacy cell geometry: no glyph and
 * no reserved slot, decision 2026-09-25). The root's resolved default is not
 * used, since a composed root (no `trigger`) defaults to `always` for the
 * Button it would render.
 */
export const resolveCellChevronMode = ({
    showChevron,
    root,
}: {
    showChevron: boolean | undefined;
    root: Pick<PickerRootContextValue, "explicitChevron">;
}): PickerChevron => {
    if (showChevron === undefined) {
        return root.explicitChevron ?? "none";
    }
    return showChevron ? "always" : "none";
};

/** A preset's effective chevron mode: its own `showChevron` wins over the root's `chevron`. */
export const resolveChevronMode = ({
    showChevron,
    root,
}: {
    showChevron: boolean | undefined;
    root: Pick<PickerRootContextValue, "chevron">;
}): PickerChevron => {
    if (showChevron === undefined) {
        return root.chevron;
    }
    return showChevron ? "always" : "none";
};
